import {useQuery} from "@tanstack/react-query";
import {getSubjects} from "api/subject.api.ts";
import {useFilters} from "context/FilterContext";
import {onlineTypeOptions} from "conts/options";
import {filterAllowedClasses} from "conts/allowedClasses";
import { userRoute } from "pages/user/user.routes";
import { useEffect, useRef, useState } from "react";
import { useMediaQuery } from "react-responsive";
import { useNavigate } from "react-router-dom";
import styled from "styled-components";
import { AutoCompleteInputVariant } from "ui/base/autoCompleteInput/AutoCompleteInputVariant";
import { Modal } from "ui/base/modal/Modal";
import { SelectButton } from "ui/base/selectInput/SelectButton";
import { SelectFilter } from "ui/base/selectInput/SelectFilter";
import { SearchIcon } from "ui/icons";
import { defaultThemeColors, fonts } from "ui/theme";
import { getFiltersSearchDisabledMessage, isFiltersSearchDisabled } from "./filterUtils";

export const MobileFiltersSection = ({ isCompact = false }) => {
    const navigate = useNavigate();
    const {filters, setFilters, handleLocationChange} = useFilters();
    const [showDisabledHint, setShowDisabledHint] = useState(false);
    const searchButtonWrapperRef = useRef(null);

    const {data: subjectData} = useQuery({
        queryKey: ["subjects"],
        queryFn: getSubjects,
    });

    const filteredSubjects = filterAllowedClasses(subjectData);
    
    const subjects =
        filteredSubjects?.map((subject) => ({
            value: subject.id,
            label: subject.title,
        })) || [];

    const [modalOpen, setModalOpen] = useState(false);
    const openModal = () => setModalOpen(true);
    const closeModal = () => {
        setShowDisabledHint(false);
        setModalOpen(false);
    };

    const online_type = filters.online_type?.value || "";
    const isSearchDisabled = isFiltersSearchDisabled(filters);
    const disabledMessage = getFiltersSearchDisabledMessage(filters);
    const isMobile = useMediaQuery({maxWidth: "768px"});

    useEffect(() => {
        if (!showDisabledHint) return;

        const timeoutId = setTimeout(() => {
            setShowDisabledHint(false);
        }, 1800);

        return () => clearTimeout(timeoutId);
    }, [showDisabledHint]);

    useEffect(() => {
        setShowDisabledHint(false);
    }, [filters.typeOfClass, filters.online_type, filters.location]);

    useEffect(() => {
        if (!showDisabledHint || !modalOpen) return;

        const hideIfOutsideButton = (event) => {
            const target = event.target;
            if (!searchButtonWrapperRef.current?.contains(target)) {
                setShowDisabledHint(false);
            }
        };

        document.addEventListener("touchstart", hideIfOutsideButton);
        document.addEventListener("mousedown", hideIfOutsideButton);

        return () => {
            document.removeEventListener("touchstart", hideIfOutsideButton);
            document.removeEventListener("mousedown", hideIfOutsideButton);
        };
    }, [showDisabledHint, modalOpen]);

    if (!isMobile) return null;

    return (
        <Wrapper>
            <Container>
                <SearchBar onClick={openModal} isCompact={isCompact}>
                    <SearchIconWrapper>
                        <SearchIcon color={defaultThemeColors.txt}  size={isCompact ? 14 : 20} />
                    </SearchIconWrapper>
                    <SearchTextContainer>
                        <SearchTitle isCompact={isCompact}>Search for a class</SearchTitle>
                        {!isCompact && <SearchSubtitle>Any type - Anywhere</SearchSubtitle>}
                    </SearchTextContainer>
                </SearchBar>

                <Modal handleClose={closeModal} show={modalOpen}>
                    <ModalTitle>Search for a class</ModalTitle>
                    <SelectionContainer
                        onClick={() => setShowDisabledHint(false)}
                        onTouchStart={() => setShowDisabledHint(false)}
                    >
                        <SelectFilter
                            borderColor="lightBlue"
                            bgColor="white"
                            rounded
                            placeholder="Type a class"
                            value={filters.typeOfClass}
                            onChange={(subject) => {
                                setShowDisabledHint(false);
                                setFilters({typeOfClass: subject});
                            }}
                            options={subjects}
                        />
                        <SelectButton
                            placeholder="Select Lesson Type"
                            bgColor="white"
                            valueColor="txt"
                            placeholderColor="txt"
                            chevronColor="#191D3A"
                            height="43px"
                            rounded
                            menuPlacement="top"
                            value={filters.online_type}
                            onChange={(onlineType) => {
                                setShowDisabledHint(false);
                                setFilters({online_type: onlineType});
                            }}
                            options={onlineTypeOptions}
                        />
                        {online_type === "offline" && (
                            <AutoCompleteInputVariant
                                borderColor="lightBlue"
                                placeholder="Location"
                                placeholderColor="txt"
                                bgColor="transparent"
                                rounded
                                address={
                                    filters.location
                                        ? {label: filters.location, value: filters.location}
                                        : null
                                }
                                onChange={(value) => {
                                    setShowDisabledHint(false);
                                    handleLocationChange(value);
                                }}
                            />
                        )}
                    </SelectionContainer>

                    <SearchButtonWrapper ref={searchButtonWrapperRef}>
                        {showDisabledHint && isSearchDisabled ? (
                            <DisabledHint>
                                {disabledMessage}
                            </DisabledHint>
                        ) : null}

                        <SearchButton
                            variant="secondary"
                            aria-disabled={isSearchDisabled}
                            isDisabled={isSearchDisabled}
                            onClick={() => {
                                if (isSearchDisabled) {
                                    setShowDisabledHint(true);
                                    return;
                                }
                                navigate(userRoute.searchOnline, {state: filters});
                                closeModal()
                            }}
                        >
                            Start Searching
                        </SearchButton>
                    </SearchButtonWrapper>
                </Modal>
            </Container>
        </Wrapper>
    );
};

const Wrapper = styled.div`
    width: 100%;
`;

const Container = styled.div`
    display: none;
    @media (max-width: 767px) {
        display: block;
    }
`;

const SearchBar = styled.div`
    display: flex;
    align-items: center;
    background: #ffffff;
    border-radius: 50px;
    padding: ${(props) => (props.isCompact ? "8px 14px" : "15px 20px")};
    box-shadow: 0 2px 15px rgba(0, 0, 0, 0.08);
    border: 2px solid ${defaultThemeColors.border};
    cursor: pointer;
`;

const SearchIconWrapper = styled.div`
    display: flex;
    align-items: center;
    justify-content: center;
    //padding-left: 6px;
`;

const SearchTextContainer = styled.div`
    display: flex;
    flex-direction: column;
    margin-left: 10px;
`;

const SearchTitle = styled.span`
    font-weight: ${(props) => (props.isCompact ? "400" : "600")};;
    color: ${defaultThemeColors.txt};
    font-size: ${(props) => (props.isCompact ? "13px" : "15px")};
`;

const SearchSubtitle = styled.span`
    color: #9a9aa5;
    font-size: 13px;
    margin-top: 2px;
`;

const ModalTitle = styled.h2`
    margin-bottom: 1rem;
    color: #333;
    text-align: center;
`;

const SearchButton = styled.button`
    width: 100%;
    background: ${(props) =>
            props.isDisabled ? defaultThemeColors.buttonBgNeutral : defaultThemeColors.blue};
    color: ${defaultThemeColors.white};
    padding: 12px;
    margin-top: 1rem;
    border: none;
    border-radius: 25px;
    font-size: ${fonts.sizes.small};
    font-weight: 600;
    cursor: ${(props) => (props.isDisabled ? "not-allowed" : "pointer")};
`;

const SearchButtonWrapper = styled.div`
    position: relative;
`;

const DisabledHint = styled.div`
    position: absolute;
    left: 50%;
    transform: translateX(-50%);
    bottom: calc(100% + 8px);
    background: ${defaultThemeColors.txt};
    color: ${defaultThemeColors.white};
    font-size: 12px;
    padding: 8px 10px;
    border-radius: 8px;
    white-space: nowrap;
    z-index: 10;
`;

const SelectionContainer = styled.div`
    display: grid;
    grid-template-columns: 1fr;
    grid-row-gap: 1rem;
`;
